// src/admin/AdminOrders.jsx
import { useEffect, useState } from "react";
import { apiRequest } from "../services/apiClient";
import { downloadAdminPdf } from "./downloadAdminPdf";

const STATUS_OPTIONS = [
  { value: "pending",       label: "Pending Review" },
  { value: "processing",    label: "Processing" },
  { value: "in-production", label: "In Production" },
  { value: "dispatched",    label: "Dispatched" },
  { value: "delivered",     label: "Delivered" },
  { value: "cancelled",     label: "Cancelled" },
  { value: "rejected",      label: "Rejected" },
];

const STATUS_STYLES = {
  pending:         { bg: "rgba(239,68,68,0.15)",   color: "#f87171" },
  processing:      { bg: "rgba(245,194,0,0.15)",   color: "#f5c200" },
  "in-production": { bg: "rgba(168,85,247,0.15)",  color: "#c084fc" },
  dispatched:      { bg: "rgba(34,197,94,0.15)",   color: "#4ade80" },
  delivered:       { bg: "rgba(16,185,129,0.18)",  color: "#10d9a0" },
  cancelled:       { bg: "rgba(239,68,68,0.15)",   color: "#f87171" },
  rejected:        { bg: "rgba(239,68,68,0.22)",   color: "#fb7185" },
};

const CloseIcon  = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const PrinterIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>;
const FileIcon   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;
const LinkIcon   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>;
const SendIcon   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>;

function StatusBadge({ status }) {
  var s = STATUS_STYLES[status] || STATUS_STYLES.processing;
  var label = (STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0]).label;
  return (
    <span style={{ background: s.bg, color: s.color, padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

function formatAmount(cents) {
  if (!cents) return "—";
  return "$" + (cents / 100).toFixed(2);
}

function OrderDetailModal({ order, onClose, onUpdateStatus, updating, onApprove, onReject, actionId }) {
  if (!order) return null;

  var date = order.createdAt?.toDate
    ? order.createdAt.toDate().toLocaleString("en-CA", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

  var ship = order.shippingInfo || {};
  var cartItems = order.cartItems || [];

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h3 style={{ color: "#fff", fontWeight: 800, fontSize: 20, margin: 0 }}>Order Details</h3>
            <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 12, marginTop: 4, fontFamily: "monospace" }}>
              MySQL order: #{order.id}
            </p>
            {order.sinaliteOrderId && (
              <p style={{ color: "var(--cyan)", fontSize: 12, marginTop: 2, fontFamily: "monospace" }}>
                Sinalite: #{order.sinaliteOrderId}
              </p>
            )}
          </div>
          <button onClick={onClose} style={closeBtnStyle}><CloseIcon /></button>
        </div>

        {/* Status */}
        <div style={statusBoxStyle}>
          <div>
            <p style={labelStyle}>Current Status</p>
            <StatusBadge status={order.status || "processing"} />
            {order.sinaliteStatus && (
              <span style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 4, display: "block" }}>
                Sinalite: {order.sinaliteStatus}
              </span>
            )}
          </div>
          <div style={{ flex: 1, maxWidth: 240 }}>
            <p style={labelStyle}>Update Status</p>
            <select
              value={order.status || "processing"}
              onChange={e => onUpdateStatus(order.id, e.target.value)}
              disabled={updating === order.id}
              style={selectStyle(updating === order.id)}
            >
              {STATUS_OPTIONS.map(s => (
                <option key={s.value} value={s.value} style={{ background: "#1a1a2e", color: "#fff" }}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Approval gate: supplier submission only happens after approval. */}
        {order.status === "pending" && (
          <div style={{ ...sectionStyle, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 10, padding: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <p style={{ ...labelStyle, marginBottom: 4 }}>Order Review</p>
              <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 12.5, margin: 0 }}>
                Review the artwork and order details. Approval sends the order to the supplier and emails the customer.
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={() => onReject(order.id)} disabled={actionId === order.id} style={rejectBtnStyle(actionId === order.id)}>Reject</button>
              <button onClick={() => onApprove(order.id)} disabled={actionId === order.id} style={approveBtnStyle(actionId === order.id)}>
                <SendIcon /> {actionId === order.id ? "Processing..." : "Approve & Send"}
              </button>
            </div>
          </div>
        )}

        {/* Sinalite Info */}
        {order.sinaliteOrderId && (
          <div style={{ ...sectionStyle, background: "rgba(0,170,221,0.06)", border: "1px solid rgba(0,170,221,0.15)", borderRadius: 10, padding: 14 }}>
            <p style={labelStyle}>Sinalite Order</p>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span style={{ color: "var(--cyan)", fontFamily: "monospace", fontSize: 14, fontWeight: 700 }}>
                #{order.sinaliteOrderId}
              </span>
              <a
                href={`https://apifrontend.sinaliteuppy.com/index.php`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "var(--cyan)", fontSize: 12, textDecoration: "none", background: "rgba(0,170,221,0.12)", border: "1px solid rgba(0,170,221,0.3)", padding: "4px 10px", borderRadius: 6 }}
              >
                <LinkIcon /> View on Sinalite
              </a>
            </div>
          </div>
        )}

        {/* Customer + Date */}
        <div style={sectionStyle}>
          <p style={labelStyle}>Customer</p>
          <div style={infoGridStyle}>
            <div><span style={infoLabelStyle}>Name</span><p style={infoValueStyle}>{ship.ShipFName} {ship.ShipLName}</p></div>
            <div><span style={infoLabelStyle}>Email</span><p style={infoValueStyle}>{ship.ShipEmail || "—"}</p></div>
            <div><span style={infoLabelStyle}>Phone</span><p style={infoValueStyle}>{ship.ShipPhone || "—"}</p></div>
            <div><span style={infoLabelStyle}>Order Date</span><p style={infoValueStyle}>{date}</p></div>
          </div>
        </div>

        {/* Shipping Address */}
        <div style={sectionStyle}>
          <p style={labelStyle}>Delivery Address</p>
          <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 14, lineHeight: 1.7 }}>
            {ship.ShipAddr}{ship.ShipAddr2 ? ", " + ship.ShipAddr2 : ""}<br />
            {ship.ShipCity}, {ship.ShipState} {ship.ShipZip}<br />
            {ship.ShipCountry}
          </p>
          {ship.ShipMethod && (
            <p style={{ color: "var(--cyan)", fontSize: 13, marginTop: 6, fontWeight: 600 }}>
              📦 {ship.ShipMethod}
            </p>
          )}
        </div>

        {/* Items */}
        <div style={sectionStyle}>
          <p style={labelStyle}>Items ({cartItems.length})</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {cartItems.map((item, i) => (
              <div key={i} style={itemCardStyle}>
                <div style={itemImgStyle}><PrinterIcon /></div>
                <div style={{ flex: 1 }}>
                  <strong style={{ color: "#fff", fontSize: 14 }}>Product #{item.productId}</strong>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6 }}>
                    {Object.entries(item.optionMap || {}).map(([k, v]) => (
                      <span key={k} style={tagStyle}>{k}: {v}</span>
                    ))}
                  </div>
                  {item.artworkUrl && (
                    <a href={item.artworkUrl} target="_blank" rel="noopener noreferrer" style={artworkLinkStyle}>
                      <FileIcon /> Download Artwork
                    </a>
                  )}
                </div>
                <div style={{ color: "var(--cyan)", fontWeight: 700, fontSize: 14, whiteSpace: "nowrap" }}>
                  {/* displayPrice from cart */}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Total */}
        <div style={totalBoxStyle}>
          <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, fontWeight: 600 }}>Total Charged</span>
          <span style={{ color: "#fff", fontSize: 22, fontWeight: 800 }}>{formatAmount(order.amount)}</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
          <button className="adm-btn adm-btn-sm" onClick={() => downloadAdminPdf("order", order.id).catch(error => window.alert(error.message))}>Download Order PDF</button>
          <button className="adm-btn adm-btn-sm" onClick={() => downloadAdminPdf("payment", order.id).catch(error => window.alert(error.message))}>Download Payment PDF</button>
          <button className="adm-btn adm-btn-sm" onClick={() => downloadAdminPdf("shipment", order.id).catch(error => window.alert(error.message))}>Download Shipment PDF</button>
        </div>

        {order.stripePaymentIntentId && (
          <p style={{ color: "rgba(255,255,255,0.2)", fontSize: 11, marginTop: 12, fontFamily: "monospace" }}>
            Stripe: {order.stripePaymentIntentId}
          </p>
        )}
        {order.notes && (
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, marginTop: 10, fontStyle: "italic" }}>📝 {order.notes}</p>
        )}
      </div>
    </div>
  );
}

export default function AdminOrders({ initialFilter = "all" }) {
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [updating, setUpdating] = useState(null);
  const [filter, setFilter]     = useState("all");
  const [selected, setSelected] = useState(null);
  const [actionId, setActionId] = useState(null);

  async function fetchOrders() {
    try {
      setOrders(await apiRequest("/admin/orders"));
    } catch {
      alert("Failed to load orders from MySQL API.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchOrders(); }, []);
  useEffect(() => {
    setFilter(initialFilter || "all");
  }, [initialFilter]);

  async function updateStatus(id, status) {
    setUpdating(id);
    try {
      await apiRequest(`/admin/orders/${encodeURIComponent(id)}/status`, { method: "PATCH", body: JSON.stringify({ status }) });
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
      setSelected(prev => prev?.id === id ? { ...prev, status } : prev);
    } catch {
      alert("Failed to update status.");
    } finally {
      setUpdating(null);
    }
  }

  async function approveOrder(id) {
    setActionId(id);
    try {
      var data = await apiRequest(`/admin/orders/${encodeURIComponent(id)}/approve`, { method: "POST" });
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status: data.status, sinaliteOrderId: data.sinaliteOrderId } : o));
      setSelected(prev => prev?.id === id ? { ...prev, status: data.status, sinaliteOrderId: data.sinaliteOrderId } : prev);
    } catch (err) {
      alert(err.message || "Failed to approve order.");
    } finally {
      setActionId(null);
    }
  }

  async function rejectOrder(id) {
    var reason = window.prompt("Reason for rejecting this order:", "Please contact us so we can discuss the required changes.");
    if (reason === null) return;
    setActionId(id);
    try {
      await apiRequest(`/admin/orders/${encodeURIComponent(id)}/reject`, { method: "POST", body: JSON.stringify({ reason }) });
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status: "rejected", rejectionReason: reason } : o));
      setSelected(prev => prev?.id === id ? { ...prev, status: "rejected", rejectionReason: reason } : prev);
    } catch (err) {
      alert(err.message || "Failed to reject order.");
    } finally {
      setActionId(null);
    }
  }

  var filtered = filter === "all" ? orders : orders.filter(o => {
    if (["pending", "processing", "in-production", "dispatched", "delivered", "cancelled", "rejected"].includes(filter)) return (o.status || "processing") === filter;
    if (filter === "payments") return Boolean(o.stripePaymentIntentId);
    if (filter === "supplier") return Boolean(o.sinaliteOrderId);
    if (filter === "shipped") return ["dispatched", "delivered"].includes(o.status);
    return true;
  });
  var counts = {};
  STATUS_OPTIONS.forEach(s => { counts[s.value] = orders.filter(o => (o.status || "processing") === s.value).length; });

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: "rgba(255,255,255,0.4)" }}>Loading orders...</div>;

  return (
    <div>
      {/* Filter Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        <button onClick={() => setFilter("all")} style={filterBtnStyle(filter === "all")}>All ({orders.length})</button>
        <button onClick={() => setFilter("payments")} style={filterBtnStyle(filter === "payments")}>Payments ({orders.filter(o => o.stripePaymentIntentId).length})</button>
        <button onClick={() => setFilter("supplier")} style={filterBtnStyle(filter === "supplier")}>Sent to Supplier ({orders.filter(o => o.sinaliteOrderId).length})</button>
        <button onClick={() => setFilter("shipped")} style={filterBtnStyle(filter === "shipped")}>Shipped Out ({orders.filter(o => ["dispatched", "delivered"].includes(o.status)).length})</button>
        {STATUS_OPTIONS.map(s => (
          <button key={s.value} onClick={() => setFilter(s.value)} style={filterBtnStyle(filter === s.value)}>
            {s.label} ({counts[s.value] || 0})
          </button>
        ))}
      </div>

      <div className="adm-card">
        <div className="adm-card-header">
          <span className="adm-card-title">Orders ({filtered.length})</span>
        </div>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Sinalite ID</th>
                <th>Items</th>
                <th>Shipping</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: "center", color: "rgba(255,255,255,0.3)", padding: 40 }}>No orders found.</td></tr>
              )}
              {filtered.map(o => {
                var ship = o.shippingInfo || {};
                var dateValue = o.createdAt?.toDate ? o.createdAt.toDate() : o.createdAt;
                var date = dateValue
                  ? new Date(dateValue).toLocaleDateString("en-CA", { day: "numeric", month: "short", year: "numeric" })
                  : "—";
                return (
                  <tr key={o.id} onClick={() => setSelected(o)} style={{ cursor: "pointer" }}>
                    <td>
                      <div style={{ color: "white", fontWeight: 600 }}>{ship.ShipFName} {ship.ShipLName}</div>
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>{ship.ShipEmail}</div>
                    </td>
                    <td>
                      {o.sinaliteOrderId
                        ? <span style={{ color: "var(--cyan)", fontFamily: "monospace", fontSize: 13 }}>#{o.sinaliteOrderId}</span>
                        : <span style={{ color: "rgba(255,255,255,0.25)", fontSize: 12 }}>—</span>
                      }
                    </td>
                    <td style={{ color: "rgba(255,255,255,0.7)", fontSize: 13 }}>
                      {(o.cartItems || []).length} item{(o.cartItems || []).length !== 1 ? "s" : ""}
                    </td>
                    <td style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>{ship.ShipMethod || "—"}</td>
                    <td style={{ color: "white", fontWeight: 700 }}>{formatAmount(o.amount)}</td>
                    <td>
                      <StatusBadge status={o.status || "processing"} />
                      {o.trackingNumber && (
                        <div style={{ fontSize: 11, color: "#4ade80", marginTop: 4, fontFamily: "monospace" }}>
                          {o.trackingNumber}
                        </div>
                      )}
                    </td>
                    <td style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>{date}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <OrderDetailModal
          order={selected}
          onClose={() => setSelected(null)}
          onUpdateStatus={updateStatus}
          updating={updating}
          onApprove={approveOrder}
          onReject={rejectOrder}
          actionId={actionId}
        />
      )}

      <style>{`.adm-table tbody tr:hover { background: rgba(255,255,255,0.03); }`}</style>
    </div>
  );
}

function filterBtnStyle(active) {
  return {
    background: active ? "var(--cyan)" : "rgba(255,255,255,0.06)",
    color: active ? "#fff" : "rgba(255,255,255,0.6)",
    border: "1px solid " + (active ? "var(--cyan)" : "rgba(255,255,255,0.12)"),
    padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700,
    cursor: "pointer", fontFamily: "var(--font-main)", transition: "all 0.15s",
  };
}

var overlayStyle = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 };
var modalStyle = { background: "#1a1a2e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: 28, width: "100%", maxWidth: 640, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 60px rgba(0,0,0,0.5)" };
var closeBtnStyle = { background: "rgba(255,255,255,0.08)", border: "none", color: "rgba(255,255,255,0.6)", width: 34, height: 34, borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };
var statusBoxStyle = { display: "flex", alignItems: "flex-end", gap: 24, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 16, marginBottom: 16 };
var sectionStyle = { marginBottom: 20 };
var labelStyle = { color: "rgba(255,255,255,0.4)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 };
var infoGridStyle = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 };
var infoLabelStyle = { color: "rgba(255,255,255,0.35)", fontSize: 11, fontWeight: 600 };
var infoValueStyle = { color: "#fff", fontSize: 14, marginTop: 2 };
var itemCardStyle = { display: "flex", alignItems: "flex-start", gap: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 12 };
var itemImgStyle = { width: 44, height: 44, background: "rgba(255,255,255,0.06)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.3)", flexShrink: 0 };
var tagStyle = { background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.7)", padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 600 };
var artworkLinkStyle = { display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8, background: "rgba(0,170,221,0.12)", border: "1px solid rgba(0,170,221,0.35)", color: "var(--cyan)", padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 700, textDecoration: "none" };
var totalBoxStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(0,170,221,0.08)", border: "1px solid rgba(0,170,221,0.2)", borderRadius: 10, padding: "16px 18px", marginTop: 8 };
function selectStyle(disabled) { return { width: "100%", background: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, padding: "8px 12px", color: "white", fontFamily: "var(--font-main)", fontSize: 13, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1 }; }
function sendBtnStyle(disabled) {
  return {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: disabled ? "rgba(239,68,68,0.3)" : "#ef4444",
    color: "#fff", border: "none", borderRadius: 8,
    padding: "9px 16px", fontSize: 13, fontWeight: 700,
    fontFamily: "var(--font-main)", cursor: disabled ? "not-allowed" : "pointer",
    whiteSpace: "nowrap", flexShrink: 0,
  };
}

function rejectBtnStyle(disabled) {
  return {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: disabled ? "rgba(239,68,68,0.2)" : "rgba(239,68,68,0.85)",
    color: "#fff", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
    padding: "9px 16px", fontSize: 13, fontWeight: 700,
    fontFamily: "var(--font-main)", cursor: disabled ? "not-allowed" : "pointer",
    whiteSpace: "nowrap", flexShrink: 0,
  };
}

function approveBtnStyle(disabled) {
  return {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: disabled ? "rgba(34,197,94,0.25)" : "#22c55e",
    color: "#fff", border: "none", borderRadius: 8,
    padding: "9px 16px", fontSize: 13, fontWeight: 700,
    fontFamily: "var(--font-main)", cursor: disabled ? "not-allowed" : "pointer",
    whiteSpace: "nowrap", flexShrink: 0,
  };
}
